import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import {
  Alert, Button, Card, Empty, Modal, Popconfirm, Select, Skeleton, Space,
  Statistic, Table, Tabs, Tag, Typography, message,
} from 'antd';
import { MoneyField, MonthField } from '../../../ui/fields';
import { Field, FormModal, FormSection } from '../../../ui/forms';
import { LockOutlined, PlusOutlined, ThunderboltOutlined } from '@ant-design/icons';
import type {
  PayrollRunRow, PayrollRunStatus, PayslipRow, SalaryStructureRow, UpsertSalaryStructureRequest,
} from '@velora/contracts';
import { extractApiMessage, productApi } from '../../../api';

/**
 * Admin surface — Payroll.
 * Two tabs: Salary structure (per-staff basic/allowances/deductions) and
 * Runs (monthly payroll cycle: generate → lock → paid). Payroll is derived
 * (M-08); a locked month refuses re-generation (M-09).
 */
export function PayrollPage() {
  const { institution = '' } = useParams<{ institution: string }>();

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div>
        <Typography.Title level={3} style={{ margin: 0 }}>Payroll</Typography.Title>
        <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
          Salary structure per staff → generate a monthly run → lock → mark paid. Payroll is derived, never typed (M-08); locked months are read-only (M-09).
        </Typography.Text>
      </div>

      <Tabs
        defaultActiveKey="runs"
        items={[
          { key: 'runs', label: 'Monthly runs', children: <RunsTab institution={institution} /> },
          { key: 'structure', label: 'Salary structure', children: <StructureTab institution={institution} /> },
        ]}
      />
    </Space>
  );
}

function RunsTab({ institution }: { institution: string }) {
  const qc = useQueryClient();
  const [genOpen, setGenOpen] = useState(false);
  const [detailMonth, setDetailMonth] = useState<string | null>(null);
  const runs = useQuery({
    queryKey: ['payroll-runs', institution],
    queryFn: () => productApi.listPayrollRuns(institution),
    enabled: !!institution,
  });
  const status = useMutation({
    mutationFn: (v: { month: string; status: 'locked' | 'paid' }) =>
      productApi.updatePayrollRunStatus(institution, v.month, { status: v.status }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['payroll-runs', institution] }); },
    onError: (e) => void message.error(extractApiMessage(e) ?? 'Update failed'),
  });

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button type="primary" icon={<ThunderboltOutlined />} onClick={() => setGenOpen(true)}>
          Generate month
        </Button>
      </div>

      {runs.isLoading && <Skeleton active paragraph={{ rows: 4 }} />}
      {(runs.data?.rows.length ?? 0) === 0 && !runs.isLoading && (
        <Empty description={<span>No payroll runs yet. <a onClick={() => setGenOpen(true)}>Generate the first month</a>.</span>} style={{ padding: 20 }} />
      )}
      {runs.data && runs.data.rows.length > 0 && (
        <Card size="small" title={`${runs.data.total} runs`}>
          <Table<PayrollRunRow>
            size="small"
            pagination={false}
            rowKey="id"
            dataSource={runs.data.rows}
            onRow={(r) => ({ onClick: () => setDetailMonth(r.month), style: { cursor: 'pointer' } })}
            columns={[
              { title: 'Month', dataIndex: 'month', key: 'm', render: (v: string) => <b style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>{v}</b> },
              { title: 'Staff', dataIndex: 'staff_count', key: 'c', width: 90, align: 'right' },
              { title: 'Gross', dataIndex: 'total_gross_paise', key: 'g', width: 130, align: 'right', render: (v: number) => `₹${paise(v)}` },
              { title: 'Net', dataIndex: 'total_net_paise', key: 'n', width: 130, align: 'right', render: (v: number) => <b>₹{paise(v)}</b> },
              { title: 'Status', dataIndex: 'status', key: 's', width: 100, render: (v: PayrollRunStatus) => <Tag color={v === 'paid' ? 'success' : v === 'locked' ? 'processing' : 'default'}>{v}</Tag> },
              {
                key: 'action', width: 160, align: 'right',
                render: (_, r) => (
                  <Space size={4} onClick={(e) => e.stopPropagation()}>
                    {r.status === 'draft' && (
                      <Popconfirm title="Lock this month?" description="Locked months can't be re-generated (M-09)." onConfirm={() => status.mutate({ month: r.month, status: 'locked' })} okText="Lock">
                        <Button size="small" icon={<LockOutlined />}>Lock</Button>
                      </Popconfirm>
                    )}
                    {r.status === 'locked' && (
                      <Button size="small" type="primary" onClick={() => status.mutate({ month: r.month, status: 'paid' })}>Mark paid</Button>
                    )}
                  </Space>
                ),
              },
            ]}
          />
        </Card>
      )}

      <GenerateModal
        open={genOpen}
        institution={institution}
        onClose={() => setGenOpen(false)}
        onDone={(month) => {
          setGenOpen(false);
          void qc.invalidateQueries({ queryKey: ['payroll-runs', institution] });
          void message.success(`Generated ${month}`);
          setDetailMonth(month);
        }}
      />

      <RunDetailModal
        institution={institution}
        month={detailMonth}
        onClose={() => setDetailMonth(null)}
      />
    </Space>
  );
}

function GenerateModal({
  open, institution, onClose, onDone,
}: { open: boolean; institution: string; onClose: () => void; onDone: (month: string) => void }) {
  const now = new Date();
  const defaultMonth = `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`;
  return (
    <FormModal<{ month: string }>
      open={open} onClose={onClose}
      title="Generate payroll" submitText="Generate"
      initial={{ month: defaultMonth }}
      mutation={(v) => productApi.generatePayrollRun(institution, v)}
      onSuccess={(_r, v) => onDone(v.month)}
    >
      <Alert type="info" showIcon message="Idempotent for draft months" description="Re-generating a draft month replaces its payslips from the current salary structure. Locked months refuse." style={{ marginBottom: 12 }} />
      <Field name="month" label="Month" required>
        <MonthField />
      </Field>
    </FormModal>
  );
}

function RunDetailModal({
  institution, month, onClose,
}: { institution: string; month: string | null; onClose: () => void }) {
  const q = useQuery({
    queryKey: ['payroll-run', institution, month],
    queryFn: () => productApi.getPayrollRun(institution, month!),
    enabled: !!institution && !!month,
  });
  return (
    <Modal open={!!month} onCancel={onClose} title={month ? `Payroll · ${month}` : ''} width={1000} footer={null} destroyOnClose>
      {q.isLoading && <Skeleton active paragraph={{ rows: 6 }} />}
      {q.data && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 12, marginBottom: 12 }}>
            <Card size="small"><Statistic title="Status" value={q.data.run.status} /></Card>
            <Card size="small"><Statistic title="Staff" value={q.data.run.staff_count} /></Card>
            <Card size="small"><Statistic title="Gross" value={`₹${paise(q.data.run.total_gross_paise)}`} /></Card>
            <Card size="small"><Statistic title="Net" value={`₹${paise(q.data.run.total_net_paise)}`} valueStyle={{ color: '#1c6442' }} /></Card>
          </div>
          <Table<PayslipRow>
            size="small"
            pagination={{ pageSize: 15 }}
            rowKey="id"
            dataSource={q.data.payslips}
            columns={[
              { title: 'Staff', dataIndex: 'staff_name', key: 'n', render: (v: string) => <b>{v}</b> },
              { title: 'Role', dataIndex: 'role', key: 'r', width: 130, render: (v: string) => <Tag>{v.replace(/_/g, ' ')}</Tag> },
              { title: 'Basic', key: 'b', width: 100, align: 'right', render: (_, r) => `₹${paise(r.components.basic)}` },
              { title: 'HRA+DA+other', key: 'a', width: 130, align: 'right', render: (_, r) => `₹${paise(r.components.hra + r.components.da + r.components.other_allowance)}` },
              { title: 'Gross', dataIndex: 'gross_paise', key: 'g', width: 110, align: 'right', render: (v: number) => `₹${paise(v)}` },
              { title: 'Deductions', dataIndex: 'deductions_paise', key: 'd', width: 120, align: 'right', render: (v: number) => `₹${paise(v)}` },
              { title: 'Net', dataIndex: 'net_paise', key: 'net', width: 120, align: 'right', render: (v: number) => <b>₹{paise(v)}</b> },
            ]}
          />
        </>
      )}
    </Modal>
  );
}

function StructureTab({ institution }: { institution: string }) {
  const qc = useQueryClient();
  const [editFor, setEditFor] = useState<{ id: string; name: string; role: string; existing?: SalaryStructureRow } | null>(null);
  const q = useQuery({
    queryKey: ['salary-structure', institution],
    queryFn: () => productApi.getSalaryStructure(institution),
    enabled: !!institution,
  });

  const rows = q.data?.rows ?? [];
  const missing = q.data?.missing_staff ?? [];
  const totals = q.data?.totals ?? { gross: 0, deductions: 0, net: 0 };

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 12 }}>
        <Card size="small"><Statistic title="With structure" value={rows.length} /></Card>
        <Card size="small"><Statistic title="Missing" value={missing.length} valueStyle={{ color: missing.length > 0 ? '#a35800' : undefined }} /></Card>
        <Card size="small"><Statistic title="Monthly gross" value={`₹${paise(totals.gross)}`} /></Card>
        <Card size="small"><Statistic title="Monthly net" value={`₹${paise(totals.net)}`} valueStyle={{ color: '#1c6442' }} /></Card>
      </div>

      {missing.length > 0 && (
        <Alert
          type="warning"
          showIcon
          message={`${missing.length} active staff without a salary structure`}
          description={
            <Space wrap size={4} style={{ marginTop: 4 }}>
              {missing.map((s) => (
                <Tag key={s.id} style={{ cursor: 'pointer' }} onClick={() => setEditFor({ id: s.id, name: s.name, role: s.role })}>
                  {s.name} · {s.role}
                </Tag>
              ))}
            </Space>
          }
        />
      )}

      <Card size="small" title={`${rows.length} structures`}>
        {q.isLoading && <Skeleton active paragraph={{ rows: 4 }} />}
        {rows.length === 0 && !q.isLoading && missing.length === 0 && (
          <Empty description="No active staff yet." style={{ padding: 20 }} />
        )}
        {rows.length > 0 && (
          <Table<SalaryStructureRow>
            size="small"
            pagination={{ pageSize: 15 }}
            rowKey="id"
            dataSource={rows}
            columns={[
              { title: 'Staff', dataIndex: 'staff_name', key: 'n', render: (v: string) => <b>{v}</b> },
              { title: 'Role', dataIndex: 'role', key: 'r', width: 130, render: (v: string) => <Tag>{v.replace(/_/g, ' ')}</Tag> },
              { title: 'Basic', dataIndex: 'basic_paise', key: 'b', width: 100, align: 'right', render: (v: number) => `₹${paise(v)}` },
              { title: 'Allowances', key: 'a', width: 120, align: 'right', render: (_, r) => `₹${paise(r.hra_paise + r.da_paise + r.other_allowance_paise)}` },
              { title: 'Deductions', dataIndex: 'deductions_paise', key: 'd', width: 120, align: 'right', render: (v: number) => `₹${paise(v)}` },
              { title: 'Net', dataIndex: 'net_paise', key: 'net', width: 120, align: 'right', render: (v: number) => <b>₹{paise(v)}</b> },
              {
                key: 'action', width: 80, align: 'right',
                render: (_, r) => (
                  <Button size="small" onClick={() => setEditFor({ id: r.staff_id, name: r.staff_name, role: r.role, existing: r })}>
                    Edit
                  </Button>
                ),
              },
            ]}
          />
        )}
      </Card>

      <EditStructureModal
        target={editFor}
        institution={institution}
        onClose={() => setEditFor(null)}
        onDone={() => {
          setEditFor(null);
          void qc.invalidateQueries({ queryKey: ['salary-structure', institution] });
          void message.success('Saved');
        }}
      />
    </Space>
  );
}

type SalaryFormValues = {
  basic: number; hra: number; da: number; other_allowance: number;
  pf: number; esi: number; professional_tax: number; other_deduction: number;
};

function EditStructureModal({
  target, institution, onClose, onDone,
}: {
  target: { id: string; name: string; role: string; existing?: SalaryStructureRow } | null;
  institution: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const initial = useMemo<SalaryFormValues>(() => {
    if (!target?.existing) return { basic: 0, hra: 0, da: 0, other_allowance: 0, pf: 0, esi: 0, professional_tax: 0, other_deduction: 0 };
    const e = target.existing;
    return {
      basic: e.basic_paise / 100, hra: e.hra_paise / 100, da: e.da_paise / 100,
      other_allowance: e.other_allowance_paise / 100, pf: e.pf_paise / 100,
      esi: e.esi_paise / 100, professional_tax: e.professional_tax_paise / 100,
      other_deduction: e.other_deduction_paise / 100,
    };
  }, [target]);
  return (
    <FormModal<SalaryFormValues>
      open={!!target} onClose={onClose}
      title={target ? `Salary · ${target.name}` : ''}
      submitText="Save"
      width={640}
      initial={initial}
      mutation={(v) => productApi.upsertSalary(institution, {
        staff_id: target!.id,
        basic_paise: Math.round(v.basic * 100),
        hra_paise: Math.round(v.hra * 100),
        da_paise: Math.round(v.da * 100),
        other_allowance_paise: Math.round(v.other_allowance * 100),
        pf_paise: Math.round(v.pf * 100),
        esi_paise: Math.round(v.esi * 100),
        professional_tax_paise: Math.round(v.professional_tax * 100),
        other_deduction_paise: Math.round(v.other_deduction * 100),
      } satisfies UpsertSalaryStructureRequest)}
      onSuccess={onDone}
    >
      <FormSection title="Earnings" cols={4}>
        <Field name="basic" label="Basic" required><MoneyField /></Field>
        <Field name="hra" label="HRA"><MoneyField /></Field>
        <Field name="da" label="DA"><MoneyField /></Field>
        <Field name="other_allowance" label="Other allow."><MoneyField /></Field>
      </FormSection>
      <FormSection title="Deductions" cols={4}>
        <Field name="pf" label="PF"><MoneyField /></Field>
        <Field name="esi" label="ESI"><MoneyField /></Field>
        <Field name="professional_tax" label="Prof. tax"><MoneyField /></Field>
        <Field name="other_deduction" label="Other ded."><MoneyField /></Field>
      </FormSection>
    </FormModal>
  );
}

function paise(n: number): string {
  return (n / 100).toLocaleString('en-IN', { maximumFractionDigits: 0 });
}
